import useAuth from 'hooks/useAuth';
import useTenantFlags from 'hooks/useTenantFlags';
import { useAppApi } from 'hooks/useAppApi';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { CLIENT_TABS_REGISTRY, getTabComponent, isProTab } from './clientTabsRegistry';

const ClientTabsContext = createContext(null);

// Mapea el userType del AuthProvider (strings distintos) al userType de
// client_tabs. Solo ejecutivo y administrador tienen tabs; el resto de
// roles recibe un array vacío.
const resolveUserType = (user) => {
  if (user?.is_ejecutivo) return 'ejecutivo';
  if (user?.is_supervisor) return 'supervisor';
  if (user?.is_administrador) return 'administrador';
  return null;
};

export const ClientTabsProvider = ({ children }) => {
  const { user } = useAuth() ?? {};
  const { isPro } = useTenantFlags();
  const userType = resolveUserType(user);

  const mapResults = useCallback((result) => {
    if (!Array.isArray(result)) return [];
    // Ordena por `order` (el backend ya ordena, pero defensivo por si cambia).
    return [...result].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, []);

  const { data, isLoading, error, hasCalled, call } = useAppApi({
    baseUrl: '/client-tabs/me',
    mapResults,
  });

  useEffect(() => {
    // Solo fetch cuando hay user autenticado con rol mapeable.
    if (!userType) return;
    if (!hasCalled) call();
  }, [userType, hasCalled, call]);

  const refresh = useCallback(() => call(), [call]);

  // Filtra tabs a los que el registry tiene componente para el userType
  // actual — evita renderizar un tab cuya UI no está implementada.
  // En plan lite:
  //   - administrador: los tabs Pro (hallazgos, odt) se OCULTAN (portal solo-consulta,
  //     no puede cambiar de plan, el upsell no aplica).
  //   - ejecutivo y otros: los tabs Pro se muestran con `locked: true` para que
  //     las páginas desplieguen ProLockOverlay (es el dueño que decide contratar).
  const tabs = useMemo(() => {
    if (!userType || !Array.isArray(data)) return [];
    return data
      .filter((t) => getTabComponent(t.key, userType) !== null)
      .filter((t) => (userType === 'administrador' ? isPro || !isProTab(t.key) : true))
      .map((t) => ({ ...t, locked: !isPro && isProTab(t.key) }));
  }, [data, userType, isPro]);

  const value = useMemo(
    () => ({
      tabs,
      rawTabs: data ?? [],
      loading: isLoading,
      error,
      userType,
      refresh,
      registry: CLIENT_TABS_REGISTRY,
    }),
    [tabs, data, isLoading, error, userType, refresh]
  );

  return <ClientTabsContext.Provider value={value}>{children}</ClientTabsContext.Provider>;
};

export const useClientTabs = () => {
  const ctx = useContext(ClientTabsContext);
  if (!ctx) {
    throw new Error('useClientTabs debe usarse dentro de ClientTabsProvider');
  }
  return ctx;
};
